import { v4 as uuidv4 } from "uuid";

/** Determines the initAction sent on a WebSocket ping. */
export type InitAction = "NEW_PROJECT" | "NEW_SESSION" | "CONTINUE_SESSION";

export interface SessionResolution {
  initAction: InitAction;
  sessionId: string;
}

/**
 * Determines the initAction and sessionId based on:
 * - Whether this is a new project (from homepage navigation)
 * - Whether the existing project has an active or expired session
 */
export function resolveSession(
  isNewProject: boolean,
  navSessionId: string | undefined,
  latestSessionId: string | undefined,
  latestSessionExpiresAt: string | undefined,
): SessionResolution {
  // Scenario 1: New project — first message, session was already created by HomePage
  if (isNewProject && navSessionId) {
    return {
      initAction: "NEW_PROJECT",
      sessionId: navSessionId,
    };
  }

  // Subsequent messages in the same component mount — reuse the session from a previous message
  // (e.g., second message on a new project before project data is fetched from API)
  if (navSessionId) {
    return {
      initAction: "CONTINUE_SESSION",
      sessionId: navSessionId,
    };
  }

  // Scenario 2: Existing project with active session from API data
  if (latestSessionId && latestSessionExpiresAt) {
    const expiresAt = new Date(latestSessionExpiresAt);
    if (expiresAt > new Date()) {
      return {
        initAction: "CONTINUE_SESSION",
        sessionId: latestSessionId,
      };
    }
  }

  // Scenario 3: No session or expired session — create new one
  return {
    initAction: "NEW_SESSION",
    sessionId: uuidv4(),
  };
}
